import packageJson from '../../package.json';
import {
	buildUpdaterFeedUrl,
	normalizeUpdaterTenantKey,
	resolveUpdaterFeedUrlTemplate
} from '../utils/updaterFeed';

let electronAPIRef = null;
let configStoreRef = null;
let initialized = false;
let autoCheckedTenantKey = '';
let autoCheckedAt = 0;
let autoCheckTimer = null;
let currentVersionPromise = null;
let checkForUpdatePromise = null;
let checkForUpdateTrigger = '';
let checkForUpdateDispatched = false;
let queuedManualCheckPromise = null;
let startUpdatePromise = null;
let installUpdatePromise = null;
// The deployment script injects the update feed for the selected environment.
// An empty fallback makes missing build configuration fail explicitly.
const DEFAULT_UPDATER_FEED_URL_TEMPLATE = '';
const AUTO_CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000;
const UPDATER_FEED_URL_TEMPLATE = resolveUpdaterFeedUrlTemplate({
	explicitTemplate: process.env.VUE_APP_DESKTOP_UPDATER_FEED_URL_TEMPLATE || process.env.UPDATE_SERVER_URL || '',
	manifestTemplate: String(process.env.VUE_APP_TENANT_MANIFEST_URL_TEMPLATES || '').split(',')[0].trim(),
	defaultTemplate: DEFAULT_UPDATER_FEED_URL_TEMPLATE
});

function normalizeReleaseNotes(releaseNotes) {
	if (!releaseNotes) {
		return '';
	}
	if (typeof releaseNotes === 'string') {
		return releaseNotes.trim();
	}
	if (Array.isArray(releaseNotes)) {
		return releaseNotes.map((item) => {
			if (typeof item === 'string') {
				return item.trim();
			}
			if (!item || typeof item !== 'object') {
				return '';
			}
			const title = item.version ? `版本 ${item.version}` : '';
			const note = String(item.note || item.text || item.releaseNotes || '').trim();
			return [title, note].filter(Boolean).join('\n');
		}).filter(Boolean).join('\n\n');
	}
	if (typeof releaseNotes === 'object') {
		return String(releaseNotes.note || releaseNotes.text || '').trim();
	}
	return String(releaseNotes).trim();
}

function currentVersionFallback() {
	return packageJson.version || '';
}

function ensureStore() {
	return configStoreRef && typeof configStoreRef.setUpdaterState === 'function';
}

function patchUpdaterState(patch) {
	if (!ensureStore()) {
		return;
	}
	configStoreRef.setUpdaterState(patch);
}

function resetUpdaterState() {
	if (!configStoreRef || typeof configStoreRef.resetUpdaterState !== 'function') {
		return;
	}
	configStoreRef.resetUpdaterState(currentVersionFallback());
}

function readTenantKeyFromElectronContext() {
	if (!electronAPIRef || !electronAPIRef.tenantContext || typeof electronAPIRef.tenantContext.get !== 'function') {
		return '';
	}
	try {
		return normalizeUpdaterTenantKey(electronAPIRef.tenantContext.get());
	} catch (error) {
		return '';
	}
}

function readTenantKeyFromLocalStorage() {
	if (typeof localStorage === 'undefined') {
		return '';
	}
	try {
		return normalizeUpdaterTenantKey(localStorage.getItem('tenantKey'));
	} catch (error) {
		return '';
	}
}

function resolveCurrentTenantKey() {
	return readTenantKeyFromElectronContext()
		|| readTenantKeyFromLocalStorage()
		|| normalizeUpdaterTenantKey(configStoreRef && configStoreRef.updater ? configStoreRef.updater.tenantKey : '');
}

function resolveFeedUrlForTenant(tenantKey) {
	const normalizedTenantKey = normalizeUpdaterTenantKey(tenantKey || resolveCurrentTenantKey());
	if (!normalizedTenantKey) {
		return '';
	}
	const current = configStoreRef && configStoreRef.updater ? configStoreRef.updater : {};
	const explicitFeedUrl = normalizeUpdaterTenantKey(current.tenantKey) === normalizedTenantKey
		? current.feedUrl || ''
		: '';
	try {
		return buildUpdaterFeedUrl(normalizedTenantKey, {
			explicitFeedUrl,
			feedUrlTemplate: UPDATER_FEED_URL_TEMPLATE
		});
	} catch (error) {
		console.warn('解析应用更新源失败:', error);
		return explicitFeedUrl || '';
	}
}

function ensureElectronAPI() {
	return Boolean(electronAPIRef && typeof electronAPIRef.checkForUpdate === 'function');
}

function updateCurrentVersion() {
	if (currentVersionPromise) {
		return currentVersionPromise;
	}
	if (!electronAPIRef || typeof electronAPIRef.invoke !== 'function') {
		const version = currentVersionFallback();
		patchUpdaterState({ currentVersion: version });
		return Promise.resolve(version);
	}
	currentVersionPromise = electronAPIRef.invoke('get-app-version').then((version) => {
		const resolved = version || currentVersionFallback();
		patchUpdaterState({ currentVersion: resolved });
		return resolved;
	}).catch(() => {
		const resolved = currentVersionFallback();
		patchUpdaterState({ currentVersion: resolved });
		return resolved;
	}).finally(() => {
		currentVersionPromise = null;
	});
	return currentVersionPromise;
}

function handleUpdateState(payload = {}) {
	const current = configStoreRef && configStoreRef.updater ? configStoreRef.updater : {};
	const phase = payload.phase || 'idle';
	const trigger = payload.trigger || current.trigger || '';
	const releaseNotes = normalizeReleaseNotes(payload.releaseNotes);
	const nextState = {
		tenantKey: normalizeUpdaterTenantKey(payload.tenantKey) || current.tenantKey || resolveCurrentTenantKey(),
		feedUrl: payload.feedUrl || current.feedUrl || '',
		trigger,
		phase,
		latestVersion: payload.version || current.latestVersion || '',
		releaseNotes,
		releaseDate: payload.releaseDate || current.releaseDate || '',
		message: payload.message || '',
		errorMessage: phase === 'error' ? (payload.message || '检查应用更新失败') : '',
		checking: phase === 'checking',
		available: ['available', 'downloading', 'downloaded', 'installing'].includes(phase),
		downloading: phase === 'downloading',
		downloaded: phase === 'downloaded',
		installing: phase === 'installing',
		progressPercent: typeof payload.percent === 'number'
			? Number(payload.percent.toFixed(2))
			: ['downloaded', 'installing'].includes(phase)
				? 100
				: phase === 'downloading'
					? current.progressPercent || 0
					: 0
	};
	if (phase === 'configured') {
		nextState.configured = true;
	}
	if (phase === 'not-available') {
		nextState.available = false;
		nextState.downloaded = false;
		nextState.downloading = false;
		nextState.installing = false;
		nextState.progressPercent = 0;
		nextState.latestVersion = payload.version || current.currentVersion || '';
		nextState.releaseNotes = '';
		nextState.releaseDate = '';
	}
	if (phase === 'checking') {
		nextState.available = current.available || false;
		nextState.downloaded = false;
		nextState.downloading = false;
		nextState.installing = false;
		nextState.progressPercent = 0;
		nextState.errorMessage = '';
	}
	patchUpdaterState(nextState);
}

function configureFeedForTenant(tenantKey) {
	if (!ensureElectronAPI()) {
		return Promise.resolve(false);
	}
	const normalizedTenantKey = normalizeUpdaterTenantKey(tenantKey || resolveCurrentTenantKey());
	if (!normalizedTenantKey) {
		return Promise.reject(new Error('租户号未准备好，无法配置应用更新源'));
	}
	const feedUrl = resolveFeedUrlForTenant(normalizedTenantKey);
	return electronAPIRef.configureUpdateFeed({
		tenantKey: normalizedTenantKey,
		feedUrl
	}).then((result) => {
		patchUpdaterState({
			configured: true,
			tenantKey: normalizedTenantKey,
			feedUrl: (result && result.feedUrl) || feedUrl,
			errorMessage: ''
		});
		return true;
	});
}

function markUpdaterChecking(trigger, tenantKey, feedUrl = '') {
	patchUpdaterState({
		tenantKey: normalizeUpdaterTenantKey(tenantKey) || resolveCurrentTenantKey(),
		trigger,
		phase: 'checking',
		errorMessage: '',
		message: trigger === 'manual' ? '正在检查应用更新' : '',
		checking: true,
		downloaded: false,
		downloading: false,
		installing: false,
		progressPercent: 0,
		feedUrl: feedUrl || ''
	});
}

function checkForUpdate(trigger = 'manual') {
	if (!ensureElectronAPI()) {
		if (trigger === 'manual') {
			return Promise.reject(new Error('当前不是应用桌面端'));
		}
		return Promise.resolve(false);
	}
	const normalizedTenantKey = resolveCurrentTenantKey();
	if (!normalizedTenantKey) {
		if (trigger === 'manual') {
			return Promise.reject(new Error('租户号未准备好，请重新登录后重试'));
		}
		return Promise.resolve(false);
	}
	const feedUrl = resolveFeedUrlForTenant(normalizedTenantKey);
	if (checkForUpdatePromise) {
		if (trigger === 'manual' && checkForUpdateTrigger !== 'manual') {
			checkForUpdateTrigger = 'manual';
			markUpdaterChecking(trigger, normalizedTenantKey, feedUrl);
			if (checkForUpdateDispatched && !queuedManualCheckPromise) {
				queuedManualCheckPromise = electronAPIRef.checkForUpdate({
					trigger,
					tenantKey: normalizedTenantKey,
					feedUrl
				}).finally(() => {
					queuedManualCheckPromise = null;
				});
			}
		}
		return queuedManualCheckPromise || checkForUpdatePromise;
	}
	checkForUpdateTrigger = trigger;
	checkForUpdateDispatched = false;
	markUpdaterChecking(trigger, normalizedTenantKey, feedUrl);
	checkForUpdatePromise = updateCurrentVersion().then(() => {
		return configureFeedForTenant(normalizedTenantKey);
	}).then(() => {
		const configuredFeedUrl = resolveFeedUrlForTenant(normalizedTenantKey);
		const activeTrigger = checkForUpdateTrigger || trigger;
		markUpdaterChecking(activeTrigger, normalizedTenantKey, configuredFeedUrl);
		checkForUpdateDispatched = true;
		return electronAPIRef.checkForUpdate({
			trigger: activeTrigger,
			tenantKey: normalizedTenantKey,
			feedUrl: configuredFeedUrl
		});
	}).finally(() => {
		checkForUpdatePromise = null;
		checkForUpdateTrigger = '';
		checkForUpdateDispatched = false;
	});
	return checkForUpdatePromise;
}

function autoCheckForCurrentTenant(options = {}) {
	const tenantKey = resolveCurrentTenantKey();
	if (!tenantKey) {
		return Promise.resolve(false);
	}
	const updater = configStoreRef && configStoreRef.updater ? configStoreRef.updater : {};
	if (['available', 'downloading', 'downloaded', 'installing'].includes(updater.phase)) {
		return Promise.resolve(false);
	}
	const now = Date.now();
	if (!options.force && autoCheckedTenantKey === tenantKey && now - autoCheckedAt < AUTO_CHECK_INTERVAL_MS) {
		return Promise.resolve(false);
	}
	autoCheckedTenantKey = tenantKey;
	autoCheckedAt = now;
	return checkForUpdate('auto').catch((error) => {
		console.warn('自动检查更新失败:', error);
		return false;
	});
}

function startAutoCheckLoop() {
	if (!ensureElectronAPI() || autoCheckTimer) {
		return;
	}
	autoCheckTimer = setInterval(() => {
		autoCheckForCurrentTenant({ force: true });
	}, AUTO_CHECK_INTERVAL_MS);
}

function startUpdate() {
	if (!ensureElectronAPI()) {
		return Promise.reject(new Error('当前不是应用桌面端'));
	}
	const updater = configStoreRef && configStoreRef.updater ? configStoreRef.updater : {};
	if (updater.downloading || updater.phase === 'downloading') {
		return Promise.resolve(true);
	}
	if (startUpdatePromise) {
		return startUpdatePromise;
	}
	startUpdatePromise = electronAPIRef.startUpdate().finally(() => {
		startUpdatePromise = null;
	});
	return startUpdatePromise;
}

function installUpdate() {
	if (!ensureElectronAPI()) {
		return Promise.reject(new Error('当前不是应用桌面端'));
	}
	const updater = configStoreRef && configStoreRef.updater ? configStoreRef.updater : {};
	if (updater.installing || updater.phase === 'installing') {
		return Promise.resolve(true);
	}
	if (installUpdatePromise) {
		return installUpdatePromise;
	}
	installUpdatePromise = electronAPIRef.installUpdate().finally(() => {
		installUpdatePromise = null;
	});
	return installUpdatePromise;
}

function resetSession() {
	autoCheckedTenantKey = '';
	autoCheckedAt = 0;
	checkForUpdatePromise = null;
	checkForUpdateTrigger = '';
	checkForUpdateDispatched = false;
	queuedManualCheckPromise = null;
	startUpdatePromise = null;
	installUpdatePromise = null;
	resetUpdaterState();
	updateCurrentVersion();
}

function init(options = {}) {
	if (options.electronAPI) {
		electronAPIRef = options.electronAPI;
	}
	if (options.configStore) {
		configStoreRef = options.configStore;
	}
	resetUpdaterState();
	updateCurrentVersion();
	if (!ensureElectronAPI() || initialized || typeof electronAPIRef.onUpdateState !== 'function') {
		return;
	}
	electronAPIRef.onUpdateState((payload) => {
		handleUpdateState(payload || {});
	});
	startAutoCheckLoop();
	initialized = true;
}

export default {
	init,
	updateCurrentVersion,
	configureFeedForTenant,
	checkForUpdate,
	autoCheckForCurrentTenant,
	startUpdate,
	installUpdate,
	resetSession
};
